import React, { useState, useEffect } from 'react';
import { Wrench, Plus, Edit, Trash2, Loader2, Settings } from 'lucide-react';
import { equipmentAPI } from '../api/client';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const CATEGORIES = ['programmer', 'tool', 'machine', 'software', 'other'];
const CONDITIONS = ['excellent', 'good', 'fair', 'poor'];

export default function Equipment() {
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    category: 'tool',
    model_number: '',
    serial_number: '',
    purchase_cost: 0,
    condition: 'good',
    location: '',
    notes: '',
  });

  useEffect(() => {
    loadEquipment();
  }, []);

  const loadEquipment = async () => {
    setLoading(true);
    try {
      const data = await equipmentAPI.list();
      setEquipment(data || []);
    } catch (error) {
      toast.error('Failed to load equipment');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await equipmentAPI.create(formData);
      toast.success('Equipment added!');
      setDialogOpen(false);
      setFormData({
        name: '', category: 'tool', model_number: '', serial_number: '',
        purchase_cost: 0, condition: 'good', location: '', notes: '',
      });
      loadEquipment();
    } catch (error) {
      toast.error('Failed to add equipment');
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);

  const totalValue = equipment.reduce((sum, e) => sum + (e.purchase_cost || 0), 0);

  return (
    <div className="space-y-6 fade-in" data-testid="equipment-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold mb-2">Equipment</h1>
          <p className="text-muted-foreground">Track your tools, programmers, and machines</p>
        </div>
        <Button onClick={() => setDialogOpen(true)} className="btn-gradient-primary" data-testid="add-equipment-button">
          <Plus className="w-4 h-4 mr-2" /> Add Equipment
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Card className="stat-blue border">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Total Equipment</p>
            <p className="text-2xl font-bold">{equipment.length}</p>
          </CardContent>
        </Card>
        <Card className="stat-green border">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Total Value</p>
            <p className="text-2xl font-bold">{formatCurrency(totalValue)}</p>
          </CardContent>
        </Card>
      </div>

      {loading ? (
        <Card><CardContent className="p-12 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto" /></CardContent></Card>
      ) : equipment.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Wrench className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No equipment yet</h3>
            <p className="text-muted-foreground mb-4">Add your tools and programmers</p>
            <Button onClick={() => setDialogOpen(true)} className="btn-gradient-primary">
              <Plus className="w-4 h-4 mr-2" /> Add First Equipment
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {equipment.map((eq) => (
            <Card key={eq.id} className="card-hover" data-testid={`equipment-${eq.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-500/20 to-red-500/20 rounded-lg flex items-center justify-center">
                    <Wrench className="w-6 h-6 text-orange-500" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold">{eq.name}</h3>
                    <Badge variant="outline" className="capitalize mt-1">{eq.category}</Badge>
                  </div>
                </div>
                <div className="space-y-1 text-sm">
                  {eq.model_number && <p><span className="text-muted-foreground">Model:</span> {eq.model_number}</p>}
                  {eq.serial_number && <p><span className="text-muted-foreground">Serial:</span> {eq.serial_number}</p>}
                  {eq.location && <p><span className="text-muted-foreground">Location:</span> {eq.location}</p>}
                  <div className="flex items-center gap-2"><span className="text-muted-foreground">Condition:</span> <Badge variant="outline" className="capitalize">{eq.condition}</Badge></div>
                  <p className="text-lg font-semibold text-green-500 pt-2">{formatCurrency(eq.purchase_cost)}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Equipment</DialogTitle>
            <DialogDescription>Add a tool, programmer, or machine to your inventory</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Condition</Label>
                <Select value={formData.condition} onValueChange={(v) => setFormData({ ...formData, condition: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CONDITIONS.map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Model</Label>
                <Input value={formData.model_number} onChange={(e) => setFormData({ ...formData, model_number: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Serial Number</Label>
                <Input value={formData.serial_number} onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Cost ($)</Label>
                <Input type="number" step="0.01" value={formData.purchase_cost} onChange={(e) => setFormData({ ...formData, purchase_cost: parseFloat(e.target.value) || 0 })} />
              </div>
              <div className="space-y-2">
                <Label>Location</Label>
                <Input value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="btn-gradient-primary" disabled={submitting}>
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Equipment'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
